import { InjectionToken, ɵɵdefineInjectable, ɵsetClassMetadata, Injectable, ɵɵinject, Optional, Inject, ɵɵdefineComponent, ɵɵelementStart, ɵɵtext, ɵɵelementEnd, Component, APP_INITIALIZER, ɵɵdefineNgModule, ɵɵdefineInjector, ɵɵsetNgModuleScope, NgModule } from '@angular/core';
import { HttpHeaders, HttpClient, HttpClientModule } from '@angular/common/http';
import { throwError } from 'rxjs';
import { UAParser } from 'ua-parser-js';
import { Meta } from '@angular/platform-browser';

/*
 * Copyright (c) 2021 American Well Systems
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 *
 */
/**
 * ID token used to authenticate with Google Tag Manager
 */
const GOOGLE_TAG_MANAGER_TRACKING_ID = new InjectionToken('GOOGLE_TAG_MANAGER_TRACKING_ID');
const GOOGLE_TAG_MANAGER_ENABLE_DETAILED_DATA = new InjectionToken('GOOGLE_TAG_MANAGER_ENABLE_DETAILED_DATA');
const INTERNAL_LAMBDA_URL = new InjectionToken('INTERNAL_LAMBDA_URL');

// gets set during initialization
// if not set, won't fire events
class EventsConfig {
    constructor(gtmId, lambdaUrl) {
        if (gtmId) {
            this.gtmId = gtmId;
        }
        if (lambdaUrl) {
            this.lambdaUrl = lambdaUrl;
        }
    }
}

/*
 * Copyright (c) 2021 American Well Systems
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 *
 */
// Page View Event
class AwPageViewEvent {
    constructor() {
    }
}
class AwEventElement {
    constructor() {
    }
}
class AwGuestJoinedEvent {
    constructor() {
    }
}
class UserAgentAnnotated {
}
class Address {
}
class ParticipantDemoDataEvent {
}

const CelConfigMock = {
    Tenant: 'CVS',
    TLA: 'amg client patient',
    Events: [
        {
            Type: 'click',
            Url: '/welcome',
            ElementId: '',
            ElementName: '',
            SendValue: true,
            Action: 'click',
            Label: 'welcome',
            Category: 'Navigation'
        },
        {
            Type: 'click',
            Url: '/intake/patient-location',
            ElementId: '',
            ElementName: '',
            SendValue: false,
            Action: 'click',
            Label: 'intake patient location',
            Category: 'Intake'
        }
    ]
};

class CelConfigService {
    constructor() {
        this.celConfigMock = CelConfigMock;
    }
    getCelConfig() {
        return this.celConfigMock;
    }
}
CelConfigService.ɵfac = function CelConfigService_Factory(t) { return new (t || CelConfigService)(); };
CelConfigService.ɵprov = ɵɵdefineInjectable({ token: CelConfigService, factory: CelConfigService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(CelConfigService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return []; }, null); })();

/*
 * Copyright (c) 2021 American Well Systems
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 *
 */
class ClientEventsService {
    constructor(enableDetailedData = false, http, meta, celConfigService) {
        this.enableDetailedData = enableDetailedData;
        this.http = http;
        this.meta = meta;
        this.celConfigService = celConfigService;
        // Keep these constants here as other components may need to reference them and they may change
        this.eventName = 'ga_event';
        this.eventCategoryKey = 'event_category';
        this.eventLabelKey = 'event_label';
        this.eventActionKey = 'event_action';
        this.eventValueKey = 'value';
        this.eventNonInteractiveKey = 'non_interactive';
        this.eventMarketingId = 'marketingId';
        this.pageViewName = 'page_view';
        this.pageViewPath = 'page_path';
        this.pageViewLocation = 'page_location';
        this.pageViewTitle = 'page_title';
        this.conf = new EventsConfig();
        this.marketingId = localStorage.getItem(this.eventMarketingId);
        this.celConfig = celConfigService.getCelConfig();
    }
    /**
     * Initialize the dataLayer global variable and record an initial `gtm.js` event.
     *
     * This should be run before importing the Google Tag Manager gtm.js file.
     */
    init() {
        // GTM requires the dataLayer global be initialized before loading the JS script
        this.tag({
            event: 'gtm.js',
            'gtm.start': new Date().getTime()
        });
    }
    /**
     * Send a custom event to Google Tag Manager, with the event name `ga_event`.
     *
     * This is equivalent to:
     * ```js
     * dataLayer.push({
     *   event: 'ga_event',
     *   event_category: category,
     *   event_action: action,
     *   event_label: label,
     *   event_value: value,
     *   non_interactive: nonInteractive,
     *   ...customDimensions
     * });
     * ```
     */
    gaEvent(action, category, label, value, nonInteractive, customDimensions) {
        if (this.marketingId) {
            if (customDimensions === undefined) {
                customDimensions = {};
            }
            customDimensions['marketingId'] = this.marketingId;
        }
        this.tag(Object.assign({ event: this.eventName, [this.eventActionKey]: action, [this.eventCategoryKey]: category, [this.eventLabelKey]: label, [this.eventValueKey]: this.enableDetailedData ? value : null, [this.eventNonInteractiveKey]: !!nonInteractive }, ((this.enableDetailedData && customDimensions) ? customDimensions : {})));
    }
    /**
     * google tag manager event to send to google analytics
     *
     * @param eventname  string event name defined in event service
     * @param event object representing the event body to be sent to lambda
     * @param token authentication token to be set in a header
     * @param isGa boolean will you send to GA
     * @param isInternal boolean will you send to internal pipeline
     * @returns Observable<any> object from internal pipeline response
     */
    event(eventname, event, token, isGa, isInternal) {
        if (isGa) {
            // TODO: send GA event
            // Check against model here
            const celEvent = this.matchConfig(event);
            if (celEvent) {
                if (celEvent.SendValue) {
                    this.gaEvent(celEvent.Action, celEvent.Category, celEvent.Label, celEvent.Value);
                }
                else {
                    this.gaEvent(celEvent.Action, celEvent.Category, celEvent.Label);
                }
            }
        }
        if (isInternal) {
            return this.awEvent(eventname, event, token);
        }
    }
    /**
     * Calls this.event() with eventName set for an event on an Element
     */
    eventElement(event, token, isGa, isInternal) {
        return this.event('event-element', event, token, isGa, isInternal);
    }
    /**
     * Send participant demographic data event.
     *
     * @param practitioner participant FHIR resource
     * @param patient patient FHIR resource
     * @param token authentication token to be set in a header
     * @param tenantId the Id of the tenant for this event
     * @returns Observable<any> object from internal pipeline response
     */
    demographicEvent(practitioner, patient, tenantId, token) {
        const authHeader = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        };
        const headers = new HttpHeaders(authHeader);
        if (practitioner) {
            const demoDataEvent = new ParticipantDemoDataEvent();
            demoDataEvent.participant_source_id = practitioner.reference;
            demoDataEvent.amg_flag = false;
            demoDataEvent.role = 'PRACTITIONER';
            demoDataEvent.display_name = practitioner.displayName;
            demoDataEvent.first_name = practitioner.firstName;
            demoDataEvent.last_name = practitioner.lastName;
            demoDataEvent.birth_date = practitioner.dateOfBirth;
            demoDataEvent.gender = practitioner.gender;
            demoDataEvent.address = this.getAddress(practitioner.addresses);
            demoDataEvent.provider_npi = practitioner.nationalProviderIdentifier;
            demoDataEvent.provider_specialty = '';
            demoDataEvent.provider_licenses = [];
            demoDataEvent.licensed_in_states = [];
            demoDataEvent.email_address = this.getEmailAddress(practitioner.emailAddresses);
            demoDataEvent.phone_numbers = practitioner.phoneNumbers ? practitioner.phoneNumbers : [];
            // console.log('Sending demographic data for : ', practitioner.reference);
            return this.http.post(`${this.conf.lambdaUrl}/demo-data?tenantId=${tenantId}`, demoDataEvent, { headers, responseType: 'text' });
        }
        if (patient) {
            const demoDataEvent = new ParticipantDemoDataEvent();
            demoDataEvent.participant_source_id = patient.reference;
            demoDataEvent.amg_flag = false;
            demoDataEvent.role = 'PATIENT';
            demoDataEvent.display_name = patient.displayName;
            demoDataEvent.first_name = patient.firstName;
            demoDataEvent.last_name = patient.lastName;
            demoDataEvent.birth_date = patient.dateOfBirth;
            demoDataEvent.gender = patient.gender;
            demoDataEvent.address = this.getAddress(patient.addresses);
            demoDataEvent.patient_mrn = patient.medicalRecordNumber;
            demoDataEvent.email_address = this.getEmailAddress(patient.emailAddresses);
            demoDataEvent.phone_numbers = patient.phoneNumbers ? patient.phoneNumbers : [];
            // console.log('Sending demographic data for : ', patient.reference);
            return this.http.post(`${this.conf.lambdaUrl}/demo-data?tenantId=${tenantId}`, demoDataEvent, { headers, responseType: 'text' });
        }
    }
    /**
     * Page View event to send to both pipelines
     * @param awPageViewEvent event object representing page view
     * @param token auth token to be included in header
     * @param isGA boolean send to google?
     * @param isInternal boolean send to internal?
     * @param headers optional param of key value pairs to be sent as headers
     */
    pageView(awPageViewEvent, token, isGA, isInternal, headers) {
        if (isGA && this.conf.gtmId) {
            this.gaPageView(awPageViewEvent.event_message.page_path, awPageViewEvent.event_message.page_location, awPageViewEvent.event_message.page_location);
        }
        if (isInternal && this.conf.lambdaUrl) {
            if (!this.conf.gtmId) {
                awPageViewEvent.google_client_id = '';
            }
            this.awPageView(awPageViewEvent, token, headers).subscribe(_ => { });
        }
    }
    /**
     * Guest Joined event to send to both pipelines
     * @param awGuestJoinedEvent event object representing the joinning of a guest to the call
     * @param token auth token to be included in header
     * @param guestFlowUrl guest-flow reporting url (optional)
     */
    guestJoined(event, token, guestFlowUrl) {
        var _a;
        if (!this.conf.lambdaUrl) {
            throwError('Lambda improperly configured');
            return;
        }
        else if (!token) {
            throwError('No Auth Token');
            return;
        }
        event.user_agent_annotated = this.setUserAgentAnnotated(event.user_agent);
        event.browser_encoding = (_a = this.meta.getTags('charset')[0]) === null || _a === void 0 ? void 0 : _a.attributes['charset'].nodeValue; // tslint:disable-line
        const authHeader = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        };
        const authHeaders = new HttpHeaders(authHeader);
        const url = guestFlowUrl ? guestFlowUrl : this.conf.lambdaUrl;
        this.http.post(url + '?eventName=event-guest_join_visit', event, { headers: authHeaders, responseType: 'text' }).subscribe(_ => { });
    }
    /**
     * Send a page view event to Google Tag Manager.
     * This is represented as a custom event with event name 'page_view'.
     *
     * This is equivalent to:
     * ```js
     * dataLayer.push({
     *   event: 'page_view',
     *   page_path: path,
     *   page_location: location,
     *   page_title: title,
     *   ...customDimensions
     * });
     * ```
     */
    gaPageView(path, title, loc, paramMaps, customDimensions) {
        const pageView = Object.assign({ event: this.pageViewName, [this.pageViewPath]: this.enableDetailedData ? path : this.redactParamMapsFromUrl(path, paramMaps), [this.pageViewTitle]: this.enableDetailedData ? title : this.redactParamMapsFromUrl(title, paramMaps), [this.pageViewLocation]: this.enableDetailedData ? loc : this.redactParamMapsFromUrl(loc, paramMaps) }, ((this.enableDetailedData && customDimensions) ? customDimensions : {}));
        this.tag(pageView);
    }
    /**
     * Send a custom event to Google Tag manager. This is the same as `window.dataLayer.push(event)`.
     */
    tag(event) {
        this.dataLayer.push(event);
    }
    /**
     * Send a page view event to an internal endpoint
     * @param event event object representing page view
     * @param headers object with key value pairs of headers to set
     */
    awPageView(event, token, headers) {
        var _a;
        if (!this.conf.lambdaUrl) {
            return throwError('Lambda improperly configured');
        }
        else if (!token) {
            return throwError('No Auth Token');
        }
        // CEL-36 Not having invitation ID causes issues for BI team
        if (!event.event_message.param_map) {
            const paramMap = { invitation_id: '' };
            event.event_message.param_map = paramMap;
        }
        if (!event.event_message.param_map.hasOwnProperty('invitation_id') ||
            !event.event_message.param_map['invitation_id']) { // tslint:disable-line
            event.event_message.param_map = Object.assign(event.event_message.param_map, { invitation_id: '' });
        }
        event.user_agent_annotated = this.setUserAgentAnnotated(event.user_agent);
        event.browser_encoding = (_a = this.meta.getTags('charset')[0]) === null || _a === void 0 ? void 0 : _a.attributes['charset'].nodeValue; // tslint:disable-line
        const authHeader = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        };
        const authHeaders = new HttpHeaders(authHeader);
        if (headers) {
            Object.keys(headers).forEach((key) => {
                authHeaders.append(key, headers.key);
            });
        }
        return this.http.post(this.conf.lambdaUrl + '?eventName=event-page-view', event, { headers: authHeaders, responseType: 'text' });
    }
    /**
     * Send a custom event to an internal endpoint for reporting
     */
    awEvent(eventname, awevent, token) {
        var _a;
        if (!this.conf.lambdaUrl) {
            return throwError('Lambda improperly configured');
        }
        else if (!token) {
            return throwError('No Auth Token');
        }
        else {
            awevent.user_agent_annotated = this.setUserAgentAnnotated(awevent.user_agent);
            awevent.browser_encoding = (_a = this.meta.getTags('charset')[0]) === null || _a === void 0 ? void 0 : _a.attributes['charset'].nodeValue; // tslint:disable-line
            const authHeader = {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
            };
            const headers = new HttpHeaders(authHeader);
            return this.http.post(this.conf.lambdaUrl + '?eventName=' + eventname, awevent, { headers, responseType: 'text' });
        }
    }
    /**
     * Get the Google Tag Manager global dataLayer object, initializing it if does not already exist.
     */
    get dataLayer() {
        const w = window;
        if (!w.dataLayer) {
            w.dataLayer = [];
        }
        return w.dataLayer;
    }
    /**
     * Replace all occurrences of a search string with a replacement string
     * Equivalent of String.prototype.replaceAll in newer browsers
     *
     * @example
     * ```
     * // yields 'Foo bar baz qux qUx qUz'
     * replaceAll('Foo bar baz qux quux quuz', 'uu', 'U');
     * ```
     */
    replaceAll(target, search, replacement) {
        let result = target;
        let changed = true;
        while (changed) {
            const replaced = result.replace(search, replacement);
            changed = (replaced !== result);
            result = replaced;
        }
        return result;
    }
    /**
     * Take a string (probably a URL or path) and replace all parameter values in the passed param maps with a replacement.
     * Handles occurrences of the parameters that are URL-encoded and those that are not.
     *
     * @example
     * ```
     * // prints ['kate', 'foobar']
     * console.log(pathParams.getAll('provider'));
     * // prints ['123 456']
     * console.log(queryParams.getAll('patientId'));
     * const url = 'https://test.domain/schedule/kate-foobar?patientId=123%20456&rawPatientId=123 456';
     * // prints 'https://test.domain/schedule/X-X?patientId=X&rawPatientId=X'
     * console.log(redactParamMapsFromUrl(url, [pathParams, queryParams], 'X'));
     * ```
     */
    redactParamMapsFromUrl(url, paramMaps = [], replacement = 'XXXXX') {
        let redacted = url;
        paramMaps.forEach((paramMap) => (paramMap.keys.forEach((paramKey) => {
            paramMap.getAll(paramKey).forEach((paramValue) => {
                redacted = this.replaceAll(redacted, paramValue, replacement);
                redacted = this.replaceAll(redacted, encodeURIComponent(paramValue), replacement);
            });
        })));
        return redacted;
    }
    getAddress(addresses) {
        const address = new Address();
        if (addresses instanceof Array && addresses.length >= 1) {
            const fhirAddress = addresses[0];
            if (fhirAddress && fhirAddress.extension && fhirAddress.extension.length !== 0) {
                const latLong = fhirAddress.extension[0].extension;
                const latitude = latLong && latLong.length !== 0 ? latLong.filter(value => value.url === 'latitude') : null;
                const longitude = latLong && latLong.length !== 0 ? latLong.filter(value => value.url === 'longitude') : null;
                address.latitude = latitude === null || latitude === void 0 ? void 0 : latitude.valueDecimal;
                address.longitude = longitude === null || longitude === void 0 ? void 0 : longitude.valueDecimal;
            }
            address.line = fhirAddress === null || fhirAddress === void 0 ? void 0 : fhirAddress.line[0];
            address.city = fhirAddress === null || fhirAddress === void 0 ? void 0 : fhirAddress.city;
            address.state = fhirAddress === null || fhirAddress === void 0 ? void 0 : fhirAddress.state;
            address.city = fhirAddress === null || fhirAddress === void 0 ? void 0 : fhirAddress.city;
            address.postalCode = fhirAddress === null || fhirAddress === void 0 ? void 0 : fhirAddress.postalCode;
            address.country = fhirAddress === null || fhirAddress === void 0 ? void 0 : fhirAddress.country;
        }
        return address;
    }
    getEmailAddress(emailAddresses) {
        if (emailAddresses instanceof Array && emailAddresses.length >= 1) {
            const email = emailAddresses[0];
            return email ? email.value : '';
        }
        return '';
    }
    setUserAgentAnnotated(userAgent) {
        const agent = new UAParser(userAgent);
        return {
            device_model: agent.getDevice().model || '',
            device_type: agent.getDevice().type || '',
            os: agent.getOS().name,
            os_version: agent.getOS().version,
            browser: agent.getBrowser().name,
            browser_version: agent.getBrowser().version
        };
    }
    matchConfig(event) {
        let url = event.event_message.page_path;
        if (url.includes('?')) {
            url = url.substring(0, event.event_message.page_path.lastIndexOf('?'));
        }
        const events = this.celConfig.Events.filter(m => m.Type === event.event_message.action_type &&
            m.Url === url
            && (m.ElementId === event.event_message.element_id
                || m.ElementName === event.event_message.element_name
                || (!m.ElementId && !m.ElementName)));
        if (events.length > 0) {
            const celEvent = events[0];
            if ((celEvent.ElementId || celEvent.ElementName) && celEvent.SendValue) {
                celEvent.Value = event.event_message.param_map;
            }
            return celEvent;
        }
        return null;
    }
}
ClientEventsService.ɵfac = function ClientEventsService_Factory(t) { return new (t || ClientEventsService)(ɵɵinject(GOOGLE_TAG_MANAGER_ENABLE_DETAILED_DATA, 8), ɵɵinject(HttpClient), ɵɵinject(Meta), ɵɵinject(CelConfigService)); };
ClientEventsService.ɵprov = ɵɵdefineInjectable({ token: ClientEventsService, factory: ClientEventsService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(ClientEventsService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [GOOGLE_TAG_MANAGER_ENABLE_DETAILED_DATA]
            }] }, { type: HttpClient }, { type: Meta }, { type: CelConfigService }]; }, null); })();

class ClientEventsComponent {
    constructor() { }
    ngOnInit() {
    }
}
ClientEventsComponent.ɵfac = function ClientEventsComponent_Factory(t) { return new (t || ClientEventsComponent)(); };
ClientEventsComponent.ɵcmp = ɵɵdefineComponent({ type: ClientEventsComponent, selectors: [["lib-client-events"]], decls: 2, vars: 0, template: function ClientEventsComponent_Template(rf, ctx) { if (rf & 1) {
        ɵɵelementStart(0, "p");
        ɵɵtext(1, " client-events works! ");
        ɵɵelementEnd();
    } }, encapsulation: 2 });
/*@__PURE__*/ (function () { ɵsetClassMetadata(ClientEventsComponent, [{
        type: Component,
        args: [{
                selector: 'lib-client-events',
                template: `
    <p>
      client-events works!
    </p>
  `,
                styles: []
            }]
    }], function () { return []; }, null); })();

/*
 * Copyright (c) 2021 American Well Systems
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 *
 */
// Loosely based on
// https://github.com/maxandriani/ngx-google-analytics/blob/master/projects/ngx-google-analytics/src/lib/initializers
// linting break: /google-analytics.initializer.ts
// Strongly based on
// https://gitlab.amwell.systems/dev/applications/digital-first-ng-lib/-/blob/prerelease/hull/projects/digital-first-ng-service/src
// linting break:  /lib/google-tag-manager/google-tag-manager.initializer.ts
// This function must be exported as Providers cannot reference factories as local variables
const GoogleTagManagerInitializer = (gtmPromise, lambdaPromise, gtmService) => () => {
    gtmPromise.then((gtmId) => {
        if (gtmId === null || gtmId === void 0 ? void 0 : gtmId.length) {
            gtmService.conf.gtmId = gtmId;
            const scriptEl = document.createElement('script');
            scriptEl.async = true;
            scriptEl.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
            const head = document.getElementsByTagName('head')[0];
            head.appendChild(scriptEl);
            // GTM requires the dataLayer global be initialized before loading the JS script
            gtmService.init();
        }
    });
    lambdaPromise.then((lambdaUrl) => {
        if (lambdaUrl === null || lambdaUrl === void 0 ? void 0 : lambdaUrl.length) {
            gtmService.conf.lambdaUrl = lambdaUrl;
        }
    });
};
const GOOGLE_TAG_MANAGER_INITIALIZER = {
    provide: APP_INITIALIZER,
    multi: true,
    useFactory: GoogleTagManagerInitializer,
    deps: [
        GOOGLE_TAG_MANAGER_TRACKING_ID,
        INTERNAL_LAMBDA_URL,
        ClientEventsService
    ]
};

class ClientEventsModule {
}
ClientEventsModule.ɵmod = ɵɵdefineNgModule({ type: ClientEventsModule });
ClientEventsModule.ɵinj = ɵɵdefineInjector({ factory: function ClientEventsModule_Factory(t) { return new (t || ClientEventsModule)(); }, providers: [GOOGLE_TAG_MANAGER_INITIALIZER, ClientEventsService], imports: [[
            HttpClientModule
        ]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵɵsetNgModuleScope(ClientEventsModule, { declarations: [ClientEventsComponent], imports: [HttpClientModule], exports: [ClientEventsComponent] }); })();
/*@__PURE__*/ (function () { ɵsetClassMetadata(ClientEventsModule, [{
        type: NgModule,
        args: [{
                declarations: [ClientEventsComponent],
                imports: [
                    HttpClientModule
                ],
                providers: [GOOGLE_TAG_MANAGER_INITIALIZER, ClientEventsService],
                exports: [ClientEventsComponent]
            }]
    }], null, null); })();

/*
 * Public API Surface of client-events
 */

/**
 * Generated bundle index. Do not edit.
 */

export { Address, AwEventElement, AwGuestJoinedEvent, AwPageViewEvent, ClientEventsComponent, ClientEventsModule, ClientEventsService, GOOGLE_TAG_MANAGER_ENABLE_DETAILED_DATA, GOOGLE_TAG_MANAGER_TRACKING_ID, INTERNAL_LAMBDA_URL, ParticipantDemoDataEvent, UserAgentAnnotated };
//# sourceMappingURL=aw-client-events.js.map
